import { ComponentProps, ReactNode } from 'react';
import { Skeleton, withStyles } from 'vopay';
import { BodyText, Card, Title } from 'UI';
import AccountStatus from '@/components/Entities/Accounts/AccountStatus/AccountStatus';
import CredentialsStrip from './CredentialsStrip';
import type { AccountCredentials } from './accountCredentials';
import styles from './EntityHeaderCard.module.scss';

const cn = withStyles(styles);

type EntityHeaderCardProps = {
  icon: string;
  iconAlt: string;
  name: string;
  /** Figma: H2/Large Title 28 for a partner account, H3/Large Title 24 for an account. */
  nameLevel: '2' | '3';
  /** Figma: Body/16/Regular in Text/Secondary75 under the name — the currency. */
  subtitle: ReactNode;
  /** Rendered above the name: the account page's breadcrumb trail for a sub-account. */
  aboveName?: ReactNode;
  /** Rendered under the subtitle: the partner card's View Permissions link. */
  belowName?: ReactNode;
  /** Rendered under the status date: the partner card's agreement status. */
  statusNote?: ReactNode;
  /** The row the status badge and its date read from. */
  status: ComponentProps<typeof AccountStatus>['account'];
  /** Null hides the credentials strip — the caller decides who may see it and when (Active only). */
  credentials: AccountCredentials | null;
  /** A placeholder stands in for the strip while the credentials are still on their way. */
  isLoadingCredentials?: boolean;
  'data-testid': string;
};

/**
 * The identity card at the top of an account's or a partner account's drawer and page (Figma
 * "Heading Card", spec 5.5): icon, name over its currency, status with its date, and the API
 * credentials strip along the bottom when the caller passes credentials. `AccountCard` and
 * `PartnerCard` are the two thin wrappers that pick the icon, copy and heading level.
 */
export default function EntityHeaderCard({
  icon,
  iconAlt,
  name,
  nameLevel,
  subtitle,
  aboveName,
  belowName,
  statusNote,
  status,
  credentials,
  isLoadingCredentials = false,
  'data-testid': testId,
}: EntityHeaderCardProps) {
  return (
    <Card variant="section" className={cn('card')} data-testid={testId}>
      <div className={cn('header', `headingLevel${nameLevel}`)}>
        <div className={cn('identity')}>
          <img src={icon} alt={iconAlt} className={cn('icon')} />

          <div className={cn('name')}>
            {aboveName}

            <Title level={nameLevel} className={cn('title')}>
              {name}
            </Title>

            <BodyText className={cn('subtitle')}>{subtitle}</BodyText>
            {belowName}
          </div>
        </div>

        <div className={cn('status')}>
          <AccountStatus account={status} activeDateCopy="activatedOn" layout="card" />

          {statusNote && (
            <span className={cn('statusNote')} data-testid="statusNote">
              {statusNote}
            </span>
          )}
        </div>
      </div>

      {isLoadingCredentials ? (
        <Skeleton height="72px" containerClassName={cn('credentialsSkeleton')} data-testid="credentialsSkeleton" />
      ) : (
        credentials && <CredentialsStrip credentials={credentials} />
      )}
    </Card>
  );
}
